import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { CurrentUser } from '../entities/Login/CurrentUser.ts';
import Cookies from 'js-cookie';
import i18n from 'i18next';

export interface ISessionContext {
    currentUser: CurrentUser | undefined;
    setCurrentUser: Dispatch<SetStateAction<CurrentUser | undefined>>;
}

export const sessionContext = React.createContext<ISessionContext>({
    currentUser: undefined,
    setCurrentUser: () => {},
});

type SessionContextProps = {
    children: React.ReactNode;
};

export const SessionContextProvider: React.FC<SessionContextProps> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<CurrentUser | undefined>(undefined);

    const contextValue: ISessionContext = {
        currentUser,
        setCurrentUser,
    };

    const storedUser = useMemo(() => {
        const user = localStorage.getItem('currentUser');
        return user ? JSON.parse(user) : null;
    }, []);

    const cookie = Cookies.get('.AspNetCore.Culture');

    useEffect(() => {
        // Par exemple "c=en|uic=en"
        const result = cookie?.match(/c=([^|]+)/)?.[1]; // Extraire "en" ou "fr"
        if (result) 
            i18n.changeLanguage(result);
         else 
            console.error('Impossible de déterminer la langue à partir du cookie.');
        
        if (storedUser && !currentUser) 
            setCurrentUser(storedUser);
         else if (
            !storedUser &&
            window.location.pathname !== '/login' &&
            !window.location.pathname.includes('/resetPassword') &&
            window.location.pathname !== '/register' &&
            window.location.pathname !== '/patientProfile' &&
            window.location.pathname !== '/questionnaire' &&
            window.location.pathname !== '/subscription'
        ) {
            setCurrentUser(undefined);
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`;
        }
    }, [storedUser, currentUser, cookie]);

    return (
        <sessionContext.Provider value={contextValue}>{contextValue ? children : 'loading'}</sessionContext.Provider>
    );
};

export function useSessionProvider() {
    const context = useContext(sessionContext);
    const { currentUser, setCurrentUser } = context;

    return {
        currentUser,
        setCurrentUser,
    };
}
